import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
} from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    company: [
      { name: "About Us", href: "/about" },
      { name: "Careers", href: "/careers" },
      { name: "Contact", href: "/contact" },
      { name: "Services", href: "/services" },
    ],
    services: [
      { name: "Talent Acquisition", href: "/talent-acquisition" },
      { name: "Job Matching", href: "/job-matching" },
      { name: "Contract Staffing", href: "/contract-staffing" },
      { name: "Tech Training", href: "/tech-training" },
      { name: "HR Consulting", href: "/hr-consulting" },
      { name: "Employer Branding", href: "/employer-branding" },
      { name: "Cloud First", href: "/cloud-first" },
      { name: "Digital First", href: "/digital-first" },
      { name: "AI Powered", href: "/ai-powered" },
      { name: "Edge Ready", href: "/edge-ready" },
    ],
    resources: [
      { name: "For Companies", href: "/for-companies" },
      { name: "For Job Seekers", href: "/for-job-seekers" },
      { name: "For Employers", href: "/for-employers" },
      { name: "For Contractors", href: "/for-contractors" },
      { name: "CV Upload", href: "/cv-upload" },
      { name: "Submit Job", href: "/submit-job" },
    ],
    support: [
      // { name: 'Help Center', href: '/help' },
      { name: "Privacy Policy", href: "/privacy" },
      { name: "Terms of Service", href: "/terms" },
      // { name: 'Cookie Policy', href: '/cookies' }
    ],
  };

  const socialLinks = [
    { name: "Facebook", icon: Facebook, href: "https://facebook.com" },
    { name: "Twitter", icon: Twitter, href: "https://twitter.com" },
    { name: "LinkedIn", icon: Linkedin, href: "https://linkedin.com" },
    { name: "Instagram", icon: Instagram, href: "https://instagram.com" },
  ];

  const contactInfo = [
    {
      icon: Mail,
      text: "info@techmind.com",
      href: "mailto:info@techmind.com",
    },
    {
      icon: Phone,
      text: "(415) 555-0123",
      href: "tel:+14155550123",
    },
    {
      icon: MapPin,
      text: "34 Tech Avenue, Suite 100 Bangalore, 94107",
      href: "https://maps.google.com",
    },
  ];

  return (
    <footer className="bg-[#12122F] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-6">
              {/* <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">T</span>
              </div> */}
              <span className="text-3xl font-bold">TechMind</span>
            </div>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Empowering businesses through innovative technology solutions that
              drive growth and transformation.
            </p>

            <div className="space-y-3">
              {contactInfo.map((contact, index) => (
                <motion.a
                  key={index}
                  href={contact.href}
                  className="flex items-center space-x-3 text-gray-300 hover:text-white transition-colors duration-200"
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <contact.icon className="w-5 h-5 text-blue-400" />
                  <span className="text-sm">{contact.text}</span>
                </motion.a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-6">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center group"
                  >
                    <ArrowRight className="w-4 h-4 mr-[-16px] opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-6">Services</h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center group"
                  >
                    <ArrowRight className="w-4 h-4 mr-[-16px] opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-6">Resources</h3>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center group"
                  >
                    <ArrowRight className="w-4 h-4 mr-[-16px] opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="max-w-md">
            <div className="w-full">
              <h3 className="text-lg font-semibold mb-4">Stay Updated</h3>
              <p className="text-gray-300 mb-4">
                Subscribe to our newsletter for the latest insights and
                opportunities.
              </p>

              <div className="md:w-full w-1/2 min-w-[260px] flex">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-r-md transition-colors duration-200 text-sm whitespace-nowrap">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © {currentYear} TechMind Infosys. All rights reserved.
            </div>
            <div className="flex space-x-4">
              <Link to="/moderator-login">
                <button className="px-6 py-0  hover:bg-blue-500 rounded transition-colors duration-200">
                  Moderator Access
                </button>
              </Link>
            </div>

            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600 transition-all duration-200"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <social.icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>

            <div className="flex space-x-6 text-sm">
              {footerLinks.support.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
